import React, { useRef, useEffect, useMemo, useState, useCallback, useImperativeHandle, forwardRef } from 'react';
import { useThree, useFrame } from '@react-three/fiber';
import * as THREE from 'three';

const CircularSpriteCloud = forwardRef(({
  atlasTextureUrl,
  atlasJsonUrl,
  radiusRange = [5, 10],
  yRange = [-2, 2],
  center = [0, 0, 0],
  onImageClick = (id) => console.log(`Clicked image ${id}`),
  rotationSpeed = 0.1,
  imageScale = 0.5,
  textScale = 0.2,
  textOffsetY = -0.1,
  maxImages = 100,
  showTextOnHover = true,
  debug = false,
  // 新增缩放相关参数
  minZoomDistance = 3,
  maxZoomDistance = 50,
  zoomSensitivity = 0.5,
}, ref) => {
  const groupRef = useRef();
  const { camera, gl } = useThree();

  const [atlasData, setAtlasData] = useState(null);
  const [texture, setTexture] = useState(null);
  const [items, setItems] = useState([]);
  const [hoveredId, setHoveredId] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  
  // 相机控制相关状态
  const cameraState = useRef({
    isMovingToTarget: false,
    originalPosition: new THREE.Vector3(),
    targetPosition: new THREE.Vector3(),
    sphereRadius: 15,
    lerpFactor: 0.1,
    moveThreshold: 0.01,
    // 新增缩放相关状态
    targetSphereRadius: 15,
    zoomLerpFactor: 0.05,
    isZooming: false,
    touchDistance: 0, // 用于触摸缩放
  });

  // 创建缓存对象
  const materialCache = useRef(new Map());
  const geometryCache = useRef(new Map());
  const textTextureCache = useRef(new Map());
  const isProcessingClick = useRef(false);
  const raycaster = useRef(new THREE.Raycaster());
  const mouse = useRef(new THREE.Vector2());

  // 暴露给父组件的方法
  useImperativeHandle(ref, () => ({
    // 根据图片ID让相机跟随图片
    focusOnImage: (id, options = {}) => {
      if (!id) {
        console.warn('focusOnImage: 未提供图片ID');
        return false;
      }

      const item = items.find(item => item.id === id);
      if (!item) {
        console.warn(`focusOnImage: 未找到ID为 ${id} 的图片`);
        return false;
      }

      const {
        resetZoom = false,
        smoothTransition = true,
        onComplete = null
      } = options;

      if (debug) {
        console.log('[DEBUG] 调用focusOnImage:', { id, options, item });
      }

      // 设置选中的图片
      setSelectedId(id);

      // 重置缩放状态
      if (resetZoom) {
        cameraState.current.sphereRadius = cameraState.current.originalPosition.distanceTo(new THREE.Vector3(...center));
        cameraState.current.targetSphereRadius = cameraState.current.sphereRadius;
        cameraState.current.isZooming = false;
      }

      // 设置相机移动参数
      if (smoothTransition) {
        cameraState.current.lerpFactor = 0.05; // 平滑过渡
      } else {
        cameraState.current.lerpFactor = 0.3; // 快速移动
      }

      // 计算初始目标位置
      const initialTarget = calculateCameraTargetOnSphere(item.position);
      cameraState.current.targetPosition.copy(initialTarget);
      cameraState.current.isMovingToTarget = true;

      // 触发点击回调
      if (onImageClick) {
        onImageClick(id);
      }

      // 如果有完成回调，设置监听
      if (onComplete) {
        const checkComplete = () => {
          if (!cameraState.current.isMovingToTarget) {
            if (debug) console.log('[DEBUG] 相机移动完成');
            onComplete(id);
          } else {
            requestAnimationFrame(checkComplete);
          }
        };
        checkComplete();
      }

      return true;
    },

    // 返回当前选中的图片ID
    getSelectedImageId: () => selectedId,

    // 获取所有图片信息
    getImageList: () => items.map(item => ({
      id: item.id,
      name: item.name,
      position: item.position.toArray()
    })),

    // 取消选中（返回原始视角）
    resetCamera: () => {
      setSelectedId(null);
      cameraState.current.isMovingToTarget = false;
      cameraState.current.isZooming = false;
      cameraState.current.sphereRadius = cameraState.current.originalPosition.distanceTo(new THREE.Vector3(...center));
      cameraState.current.targetSphereRadius = cameraState.current.sphereRadius;
      
      camera.position.copy(cameraState.current.originalPosition);
      camera.lookAt(new THREE.Vector3(...center));
      
      if (debug) {
        console.log('[DEBUG] 相机已重置');
      }
    },

    // 设置相机缩放
    setZoom: (zoomLevel) => {
      if (!selectedId) {
        console.warn('setZoom: 请先选择一张图片');
        return false;
      }

      const newRadius = THREE.MathUtils.clamp(
        zoomLevel,
        minZoomDistance,
        maxZoomDistance
      );
      
      cameraState.current.targetSphereRadius = newRadius;
      cameraState.current.isZooming = true;
      
      if (debug) {
        console.log('[DEBUG] 手动设置缩放:', { zoomLevel, newRadius });
      }
      
      return true;
    },

    // 获取当前相机状态
    getCameraState: () => ({
      selectedId,
      isMoving: cameraState.current.isMovingToTarget,
      isZooming: cameraState.current.isZooming,
      currentRadius: cameraState.current.sphereRadius,
      targetRadius: cameraState.current.targetSphereRadius,
      cameraPosition: camera.position.toArray()
    })
  }));

  // 初始化相机状态
  useEffect(() => {
    if (camera) {
      cameraState.current.originalPosition.copy(camera.position);
      
      const centerVec = new THREE.Vector3(...center);
      const distance = camera.position.distanceTo(centerVec);
      cameraState.current.sphereRadius = distance;
      cameraState.current.targetSphereRadius = distance;
      
      if (debug) {
        console.log('[DEBUG] 相机初始状态:', {
          位置: camera.position.toArray(),
          距离中心: distance,
          中心点: centerVec.toArray()
        });
      }
    }
  }, [camera, center, debug]);

  // 计算相机在球面上的目标位置
  const calculateCameraTargetOnSphere = useCallback((itemPosition, radius) => {
    const centerVec = new THREE.Vector3(...center);
    const currentRadius = radius || cameraState.current.targetSphereRadius;
    
    const direction = new THREE.Vector3().subVectors(itemPosition, centerVec).normalize();
    const intersection = new THREE.Vector3().copy(centerVec).add(
      direction.multiplyScalar(currentRadius)
    );
    
    return intersection;
  }, [center]);

  // 处理滚轮缩放
  const handleWheel = useCallback((event) => {
    if (!selectedId) return;
    
    event.preventDefault();
    event.stopPropagation();
    
    const delta = event.deltaY > 0 ? zoomSensitivity : -zoomSensitivity;
    const newRadius = THREE.MathUtils.clamp(
      cameraState.current.targetSphereRadius + delta,
      minZoomDistance,
      maxZoomDistance
    );
    
    cameraState.current.targetSphereRadius = newRadius;
    cameraState.current.isZooming = true;
    
    if (debug) {
      console.log('[DEBUG] 滚轮缩放:', { 
        当前半径: cameraState.current.sphereRadius,
        目标半径: newRadius,
        delta 
      });
    }
  }, [selectedId, zoomSensitivity, minZoomDistance, maxZoomDistance, debug]);

  // 处理触摸缩放
  const handleTouchStart = useCallback((event) => {
    if (!selectedId || event.touches.length !== 2) return;
    
    event.preventDefault();
    event.stopPropagation();
    
    const touch1 = event.touches[0];
    const touch2 = event.touches[1];
    const distance = Math.sqrt(
      Math.pow(touch2.clientX - touch1.clientX, 2) + 
      Math.pow(touch2.clientY - touch1.clientY, 2)
    );
    
    cameraState.current.touchDistance = distance;
  }, [selectedId]);

  const handleTouchMove = useCallback((event) => {
    if (!selectedId || event.touches.length !== 2) return;
    
    event.preventDefault();
    event.stopPropagation();
    
    const touch1 = event.touches[0];
    const touch2 = event.touches[1];
    const distance = Math.sqrt(
      Math.pow(touch2.clientX - touch1.clientX, 2) + 
      Math.pow(event.touches[1].clientY - touch1.clientY, 2)
    );
    
    if (cameraState.current.touchDistance > 0) {
      const delta = (distance - cameraState.current.touchDistance) * 0.01;
      const newRadius = THREE.MathUtils.clamp(
        cameraState.current.targetSphereRadius + delta * zoomSensitivity,
        minZoomDistance,
        maxZoomDistance
      );
      
      cameraState.current.targetSphereRadius = newRadius;
      cameraState.current.isZooming = true;
      cameraState.current.touchDistance = distance;
    }
  }, [selectedId, zoomSensitivity, minZoomDistance, maxZoomDistance]);

  const handleTouchEnd = useCallback((event) => {
    if (selectedId && event.touches.length < 2) {
      cameraState.current.touchDistance = 0;
    }
  }, [selectedId]);

  // 添加事件监听器
  useEffect(() => {
    const canvas = gl.domElement;
    
    const wheelHandler = (e) => handleWheel(e);
    const touchStartHandler = (e) => handleTouchStart(e);
    const touchMoveHandler = (e) => handleTouchMove(e);
    const touchEndHandler = (e) => handleTouchEnd(e);
    
    canvas.addEventListener('wheel', wheelHandler, { passive: false });
    canvas.addEventListener('touchstart', touchStartHandler, { passive: false });
    canvas.addEventListener('touchmove', touchMoveHandler, { passive: false });
    canvas.addEventListener('touchend', touchEndHandler);
    
    return () => {
      canvas.removeEventListener('wheel', wheelHandler);
      canvas.removeEventListener('touchstart', touchStartHandler);
      canvas.removeEventListener('touchmove', touchMoveHandler);
      canvas.removeEventListener('touchend', touchEndHandler);
    };
  }, [gl.domElement, handleWheel, handleTouchStart, handleTouchMove, handleTouchEnd]);

  // 加载纹理图集
  useEffect(() => {
    const loadAssets = async () => {
      try {
        const textureLoader = new THREE.TextureLoader();
        const tex = await new Promise((resolve, reject) => {
          textureLoader.load(
            atlasTextureUrl,
            (loadedTexture) => resolve(loadedTexture),
            undefined,
            (error) => reject(error)
          );
        });
       
        tex.flipY = true;
        tex.minFilter = THREE.LinearFilter;
        tex.magFilter = THREE.LinearFilter;
        setTexture(tex);

        const response = await fetch(atlasJsonUrl);
        const data = await response.json();
        setAtlasData(data);
       
      } catch (error) {
        console.error('资源加载失败:', error);
      }
    };
   
    loadAssets();
  }, [atlasTextureUrl, atlasJsonUrl]);

  // 使用 useMemo 缓存依赖项
  const radiusRangeStr = useMemo(() => JSON.stringify(radiusRange), [radiusRange]);
  const yRangeStr = useMemo(() => JSON.stringify(yRange), [yRange]);
  const centerStr = useMemo(() => JSON.stringify(center), [center]);

  // 创建项目
  const createdItems = useMemo(() => {
    if (!atlasData || !texture) return [];
   
    try {
      const { textures, width: atlasWidth, height: atlasHeight } = atlasData;
      const numImages = Math.min(textures.length, maxImages);
      const [minR, maxR] = radiusRange;
      const [minY, maxY] = yRange;
      const [centerX, centerY, centerZ] = center;
     
      const newItems = [];
     
      for (let i = 0; i < numImages; i++) {
        const texInfo = textures[i % textures.length];
        const { id, name, x, y, width, height } = texInfo;
       
        const r = Math.random() * (maxR - minR) + minR;
        const initialAngle = Math.random() * Math.PI * 2;
        const posY = Math.random() * (maxY - minY) + minY;
       
        const posX = centerX + r * Math.cos(initialAngle);
        const posZ = centerZ + r * Math.sin(initialAngle);
       
        const uvParams = [
          x / atlasWidth,
          1 - (y + height) / atlasHeight,
          width / atlasWidth,
          height / atlasHeight
        ];
       
        const materialKey = `${x}-${y}-${width}-${height}`;
        let material;
       
        if (materialCache.current.has(materialKey)) {
          material = materialCache.current.get(materialKey);
        } else {
          material = new THREE.ShaderMaterial({
            uniforms: {
              map: { value: texture },
              atlasUV: { value: new THREE.Vector4(...uvParams) },
              alpha: { value: 1.0 },
              hoverEffect: { value: 0.0 },
            },
            vertexShader: `
              varying vec2 vUv;
              uniform vec4 atlasUV;
              void main() {
                vUv = uv * atlasUV.zw + atlasUV.xy;
                gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
              }
            `,
            fragmentShader: `
              varying vec2 vUv;
              uniform sampler2D map;
              uniform float alpha;
              uniform float hoverEffect;
              uniform vec4 atlasUV;
             
              void main() {
                vec4 color = texture2D(map, vUv);
                vec2 localUv = (vUv - atlasUV.xy) / atlasUV.zw;
                vec2 circleUv = localUv * 2.0 - 1.0;
                float dist = length(circleUv);
                float smoothEdge = 0.02;
                float alphaFactor = 1.0 - smoothstep(1.0 - smoothEdge, 1.0, dist);
                float glow = hoverEffect * (1.0 - smoothstep(0.8, 1.0, dist));
                color.rgb += glow * 0.5;
                gl_FragColor = vec4(color.rgb, color.a * alpha * alphaFactor);
              }
            `,
            transparent: true,
            depthTest: true,
            depthWrite: false,
            side: THREE.DoubleSide,
          });
          materialCache.current.set(materialKey, material);
        }
       
        const aspect = width / height;
        const geometryKey = `${aspect}`;
        let geometry;
       
        if (geometryCache.current.has(geometryKey)) {
          geometry = geometryCache.current.get(geometryKey);
        } else {
          geometry = new THREE.PlaneGeometry(
            Math.max(aspect, 1) * imageScale,
            Math.max(1 / aspect, 1) * imageScale
          );
          geometryCache.current.set(geometryKey, geometry);
        }
       
        newItems.push({
          id,
          name,
          r,
          angle: initialAngle,
          y: posY,
          material,
          geometry,
          uvParams,
          position: new THREE.Vector3(posX, centerY + posY, posZ),
          textPosition: new THREE.Vector3(posX, centerY + posY + textOffsetY, posZ),
        });
      }
     
      return newItems;
     
    } catch (error) {
      console.error('创建项目时出错:', error);
      return [];
    }
  }, [
    atlasData,
    texture,
    radiusRangeStr,
    yRangeStr,
    centerStr,
    imageScale,
    textScale,
    textOffsetY,
    maxImages
  ]);

  // 同步 items 状态
  useEffect(() => {
    if (createdItems.length > 0 && createdItems !== items) {
      setItems(createdItems);
    }
  }, [createdItems, items]);

  // 处理点击任意区域退出
  useEffect(() => {
    const handleGlobalClick = (event) => {
      if (selectedId && !isProcessingClick.current) {
        const rect = gl.domElement.getBoundingClientRect();
        mouse.current.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
        mouse.current.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
        
        raycaster.current.setFromCamera(mouse.current, camera);
        
        let clickedImage = false;
        if (groupRef.current) {
          const intersects = raycaster.current.intersectObjects(groupRef.current.children.filter(child => child.type === 'Mesh'));
          if (intersects.length > 0) {
            clickedImage = true;
          }
        }
        
        if (!clickedImage) {
          setSelectedId(null);
          cameraState.current.isMovingToTarget = false;
          cameraState.current.isZooming = false;
          cameraState.current.sphereRadius = cameraState.current.originalPosition.distanceTo(new THREE.Vector3(...center));
          cameraState.current.targetSphereRadius = cameraState.current.sphereRadius;
          
          camera.position.copy(cameraState.current.originalPosition);
          camera.lookAt(new THREE.Vector3(...center));
        }
      }
    };

    if (selectedId) {
      window.addEventListener('click', handleGlobalClick);
      return () => {
        window.removeEventListener('click', handleGlobalClick);
      };
    }
  }, [selectedId, camera, gl, center]);

  // 动画帧更新
  useFrame((state, delta) => {
    if (!groupRef.current || items.length === 0) return;
   
    const group = groupRef.current;
    const angularSpeed = (rotationSpeed * Math.PI / 180) * delta;

    // 更新所有图片的位置和角度
    items.forEach((item, index) => {
      const meshIndex = index * 2;
      const textIndex = index * 2 + 1;
     
      const mesh = group.children[meshIndex];
      const text = group.children[textIndex];
     
      if (!mesh || !text) return;
     
      item.angle += angularSpeed;
     
      const newX = center[0] + item.r * Math.cos(item.angle);
      const newZ = center[2] + item.r * Math.sin(item.angle);

      item.position.set(newX, center[1] + item.y, newZ);
      mesh.position.copy(item.position);
     
      text.position.set(newX, center[1] + item.y + textOffsetY, newZ);
     
      const isHovered = hoveredId === item.id;
      item.material.uniforms.hoverEffect.value = THREE.MathUtils.lerp(
        item.material.uniforms.hoverEffect.value,
        isHovered ? 1.0 : 0.0,
        0.2
      );
     
      const isSelected = selectedId === item.id;
      text.visible = isSelected || (!showTextOnHover || isHovered);
      
      mesh.quaternion.copy(camera.quaternion);
      text.quaternion.copy(camera.quaternion);
    });

    // 处理选中状态下的相机控制
    if (selectedId) {
      const selectedItem = items.find((item) => item.id === selectedId);
      if (!selectedItem) return;

      // 平滑更新球面半径（缩放效果）
      if (cameraState.current.isZooming) {
        cameraState.current.sphereRadius = THREE.MathUtils.lerp(
          cameraState.current.sphereRadius,
          cameraState.current.targetSphereRadius,
          cameraState.current.zoomLerpFactor
        );
        
        // 检查缩放是否完成
        if (Math.abs(cameraState.current.sphereRadius - cameraState.current.targetSphereRadius) < 0.01) {
          cameraState.current.isZooming = false;
        }
      }

      // 计算当前球面半径下的相机目标位置
      const currentTarget = calculateCameraTargetOnSphere(selectedItem.position, cameraState.current.sphereRadius);
      
      if (cameraState.current.isMovingToTarget) {
        // 检查是否接近目标
        if (camera.position.distanceTo(cameraState.current.targetPosition) < cameraState.current.moveThreshold) {
          cameraState.current.isMovingToTarget = false;
        }
      } else {
        // 更新目标位置以跟随旋转
        cameraState.current.targetPosition.copy(currentTarget);
      }
      
      // 平滑移动相机到当前目标
      camera.position.lerp(cameraState.current.targetPosition, cameraState.current.lerpFactor);
      
      // 相机始终看向中心点
      camera.lookAt(new THREE.Vector3(...center));
    }
  });

  // 处理悬停
  const handlePointerOver = useCallback((event, id) => {
    event.stopPropagation();
    setHoveredId(id);
  }, []);

  const handlePointerOut = useCallback((event) => {
    event.stopPropagation();
    setHoveredId(null);
  }, []);

  // 处理图片点击
  const handleClick = useCallback((event, id, position) => {
    event.stopPropagation();
    
    isProcessingClick.current = true;
    
    if (selectedId !== id) {
      // 新选择图片
      setSelectedId(id);
      
      // 重置缩放状态
      cameraState.current.sphereRadius = cameraState.current.originalPosition.distanceTo(new THREE.Vector3(...center));
      cameraState.current.targetSphereRadius = cameraState.current.sphereRadius;
      cameraState.current.isZooming = false;
      
      // 计算初始目标位置
      const initialTarget = calculateCameraTargetOnSphere(position);
      cameraState.current.targetPosition.copy(initialTarget);
      cameraState.current.isMovingToTarget = true;
    }
    
    onImageClick(id);
    
    setTimeout(() => {
      isProcessingClick.current = false;
    }, 0);
  }, [selectedId, onImageClick, calculateCameraTargetOnSphere, center]);

  // 创建文字纹理
  const createTextTexture = useCallback((text) => {
    if (textTextureCache.current.has(text)) {
      return textTextureCache.current.get(text);
    }
    
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    const fontSize = 48;
    canvas.width = 256;
    canvas.height = 64;
    context.font = `bold ${fontSize}px Arial`;
    context.fillStyle = 'rgba(0, 0, 0, 0.7)';
    context.fillRect(0, 0, canvas.width, canvas.height);
    const width = context.measureText(text).width;
    if (width > 256) {
      canvas.width = width + 40;
      canvas.height = width / 4 + 10;
    }
    context.font = `bold ${fontSize}px Arial`;
    context.fillStyle = 'white';
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.fillText(text, canvas.width / 2, canvas.height / 2);
   
    const texture = new THREE.CanvasTexture(canvas);
    texture.minFilter = THREE.LinearFilter;
    texture.magFilter = THREE.LinearFilter;
    
    textTextureCache.current.set(text, texture);
   
    return texture;
  }, []);

  // 清理函数
  useEffect(() => {
    return () => {
      materialCache.current.forEach((material) => material.dispose());
      materialCache.current.clear();
      
      geometryCache.current.forEach((geometry) => geometry.dispose());
      geometryCache.current.clear();
      
      textTextureCache.current.forEach((tex) => tex.dispose());
      textTextureCache.current.clear();
      
      if (texture) texture.dispose();
    };
  }, [texture]);

  return (
    <group ref={groupRef}>
      {items.map((item, index) => (
        <React.Fragment key={`item-${item.id}-${index}`}>
          {/* 圆形精灵 */}
          <mesh
            geometry={item.geometry}
            material={item.material}
            position={item.position}
            onPointerOver={(e) => handlePointerOver(e, item.id)}
            onPointerOut={handlePointerOut}
            onClick={(e) => handleClick(e, item.id, item.position)}
            renderOrder={1}
          />
         
          {/* 文字标签 */}
          <sprite
            position={item.textPosition}
            scale={[textScale * 1, textScale * 0.25, 10]}
            renderOrder={2}
          >
            <spriteMaterial
              map={createTextTexture(item.name)}
              transparent={true}
              depthTest={false}
            />
          </sprite>
        </React.Fragment>
      ))}
    </group>
  );
});

export default CircularSpriteCloud;